import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import {
  backofficeLinks,
  essentialMenuItem,
} from "@/components/lib/SidebarLinks";
import { Link, useParams, useNavigate } from "react-router-dom";
import { z } from "zod";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useMemo } from "react";
import useAuth from "@/hooks/useAuth";
import {
  fetchSingleBase,
  clearBaseMessages,
  updateExistingBase,
} from "@/redux/slice/essential-menu-slice/base-slice";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import CreateDrinksSkeleton from "@/components/skeleton/create-menu-skeleton";

const baseSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  status: z.enum(["active", "inactive", "archive", "draft"]).default("active"),
  price: z.preprocess(
    (val) => {
      if (val === "" || val === undefined || val === null) return undefined;
      return Number(val);
    },
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .min(0, "Price cannot be negative")
  ),
});

function UpdateBase() {
  const { user } = useAuth();
  const { id, item_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateLoading, error, getLoading, singleBase } = useSelector(
    (state) => state.essentialMenu.bases
  );

  const form = useForm({
    resolver: zodResolver(baseSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      menu_item_id: id,
      status: "active",
    },
  });

  const matchedItem = useMemo(() => {
    return essentialMenuItem.find((item) => item.id === Number(id));
  }, [id]);

  useEffect(() => {
    if (item_id) {
      dispatch(fetchSingleBase(item_id));
    }
  }, [item_id, dispatch]);

  useEffect(() => {
    if (singleBase) {
      form.reset({
        name: singleBase.name || "",
        description: singleBase.description || "",
        price: singleBase.price || 0,
        status: singleBase.status || "active",
        menu_item_id: singleBase.menu_item_id,
      });
    }
  }, [singleBase, form]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      menu_item_id: Number(id),
      client_id: user?.client_id,
      restaurant_id: user?.restaurant_id,
    };

    try {
      dispatch(clearBaseMessages());
      const res = await dispatch(
        updateExistingBase({ id: item_id, data: payload })
      );
      if (updateExistingBase.fulfilled.match(res)) {
        toast.success("Base updated successfully.");
        form.reset();
        dispatch(clearBaseMessages());
        if (matchedItem) {
          navigate(`${matchedItem.path}/${id}`);
        } else {
          navigate("backoffice/menu/essentail-menu/drinks/1");
        }
      } else if (updateExistingBase.rejected.match(res)) {
        toast.error(res?.payload);
      }
    } catch (error) {
      toast.error("Something went wrong.");
    }
  };

  if (getLoading) return <CreateDrinksSkeleton />;

  return (
    <Layout2
      pageName={{ title: "Update Base" }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: `${matchedItem?.title}`, path: `${matchedItem?.path}/${id}` },
        {
          name: `Update Base`,
          path: `${matchedItem?.path}/${id}/update-base`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <Card className="max-w-screen-md bg-cblack6 mx-auto mt-10 text-cwhite">
        <CardHeader>
          <CardTitle className="text-lg">Update Base</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-7"
            >
              {/* Name */}
              <FormInputField
                name="name"
                style="bg-cblack7"
                placeholder="Name"
                form={form}
                type="text"
                label="Name"
              />
              <FormInputField
                name="price"
                style="bg-cblack7"
                placeholder="price"
                form={form}
                type="text"
                label="Price"
              />
              {/* Description */}
              <FormInputField
                name="description"
                style="bg-cblack7"
                placeholder="Add note"
                form={form}
                type="textarea"
                label="Description"
              />
              {/* Status */}
              <FormInputField
                name="status"
                style="bg-cblack7 h-10"
                placeholder="Select Status"
                form={form}
                type="select"
                selectOption={[
                  { label: "Select Status", value: "", disabled: true },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
                label="Status"
              />

              {/* Buttons */}
              <div className="w-full flex gap-5 items-center justify-between mt-5">
                <Link className="w-full" to={`${matchedItem.path}/${id}`}>
                  <Button className="bg-cblack7 w-full text-cwhite">
                    Cancel
                  </Button>
                </Link>
                <Button
                  isLoading={updateLoading}
                  type="submit"
                  className="bg-cgreen text-white w-full"
                >
                  Update
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </Layout2>
  );
}

export default UpdateBase;
